/* ════════════════════ M2.5 — STAGE 1 → STAGE 2 DRAWER (shared) ════════════════════
   Promotion to Stage 2 REQUIRES the signed document — you cannot mark an agreement
   "Signed & governing" without uploading the signed copy. The stage is promoted
   IN PLACE on the server (POST .../agreement/{id}/sign): the stage-1 file stays in
   documents[] and events[] records the action, so the audit trail is preserved.
   Used by both the Agreements Overview and the partner timeline. */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SyncIcon from '@mui/icons-material/Sync'
import CheckIcon from '@mui/icons-material/Check'
import {Badge, SideDrawer, FileDrop, fmtDate} from '../components'
import {apiError} from '../supplyMeta'
import {useSignAgreementMutation} from '../../../redux/supplyApiSlice'
import {agrTypeFull} from './agreementHealth'

export default function StageDrawer({open, agr: a, template, toast, onClose, onDone}) {
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [invalid, setInvalid] = useState(false)
  const [signAgreement] = useSignAgreementMutation()

  React.useEffect(() => {
    if (open) {
      setFile(null)
      setBusy(false)
      setInvalid(false)
    }
  }, [open])

  const confirm = async () => {
    if (!a) return
    if (!file) {
      setInvalid(true)
      toast('Upload the signed document to move this agreement to Stage 2.', 'error')
      return
    }
    setBusy(true)
    try {
      const res = await signAgreement({id: a.id, file}).unwrap()
      const n = (res && res.supersededIds ? res.supersededIds : []).length
      toast(
        'Updated to Stage 2: Signed — now governing.' +
          (n ? ' Superseded ' + n + ' prior agreement(s).' : '') +
          ' Stage 1 document retained for audit.',
        'success'
      )
      onDone()
    } catch (e) {
      toast(apiError(e), 'error')
      setBusy(false)
    }
  }

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title='Update to Stage 2 — Signed'
      width={440}
      footer={
        <>
          <button className='btn btn-ghost' onClick={onClose}>
            Cancel
          </button>
          <button className='btn btn-primary' disabled={!file || busy} onClick={confirm}>
            {busy ? (
              <SyncIcon className='ps-spin' style={{width: 16, height: 16}} />
            ) : (
              <CheckIcon style={{width: 16, height: 16}} />
            )}
            {busy ? 'Updating…' : 'Confirm Stage 2'}
          </button>
        </>
      }
    >
      {a ? (
        <>
          <div className='warnbox info' style={{marginBottom: 16}}>
            <InfoOutlinedIcon style={{width: 16, height: 16}} />
            <div>
              The signed document has arrived. Upload it to move <b>{a.title}</b> from Stage 1 (email
              received) to Stage 2 (signed). The Stage 1 document stays on file for audit.
            </div>
          </div>
          <dl className='dl' style={{gridTemplateColumns: 'auto 1fr', marginBottom: 16}}>
            <dt>Type</dt>
            <dd>{agrTypeFull(a)}</dd>
            <dt>Current stage</dt>
            <dd>
              <Badge status='draft' label='Stage 1: Email Received' />
            </dd>
            <dt>Effective</dt>
            <dd>
              {fmtDate(a.effectiveFrom)}
              {a.effectiveTo ? ' → ' + fmtDate(a.effectiveTo) : ' · open-ended'}
            </dd>
          </dl>
          <div className={'field' + (invalid ? ' invalid' : '')}>
            <label>
              Signed document <span className='req'>*</span>
            </label>
            <FileDrop
              accept='.pdf,.docx,.png,.jpg,.jpeg,.eml,.msg'
              maxMB={10}
              title='Drop the signed agreement here'
              hint='PDF, DOCX, PNG, JPG, EML, MSG · max 10 MB'
              sampleName='agreement-upload-template.txt'
              sampleLabel='Download template'
              sampleContent={template}
              onFile={(f) => {
                setFile(f)
                setInvalid(false)
              }}
              onError={(msg) => {
                setInvalid(true)
                toast(msg, 'error')
              }}
            />
            <div className='vmsg'>Upload the signed document to continue.</div>
          </div>
        </>
      ) : null}
    </SideDrawer>
  )
}

StageDrawer.propTypes = {
  open: PropTypes.bool,
  agr: PropTypes.object,
  template: PropTypes.string,
  toast: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
}
